import EventHandler from '@pixelunion/events';
import { transition } from '@pixelunion/animations';
import rimg from '@pixelunion/rimg-shopify';
import ThemeUtils from '../utils/ThemeUtils';
import ScrollLock from '../utils/ScrollLock';

export default class PredictiveSearch {
  constructor(el) {
    this.el = el;
    this.siteOverlay = document.querySelector('[data-site-overlay]');
    this.input = this.el.querySelector('[data-header-search-input]');
    this.searchCloseButton = this.el.querySelector('[data-search-close]');
    this.predictiveSearchContainer = this.el.querySelector('[data-predictive-search-container]');
    this.predictiveSearchResultsContainer = this.el.querySelector('[data-predictive-search-results-container]');

    this.siteOverlayAnimation = transition({ el: this.siteOverlay });
    this.predictiveSearchContainerAnimation = transition({ el: this.predictiveSearchContainer, state: 'closed' });

    this.events = new EventHandler();
    this.cachedResults = new Map();
    this.isOpen = false;

    this._setupEventListeners();
  }

  _setupEventListeners() {
    this.events.register(window, 'keyup', e => this._onKeyUp(e));
    this.events.register(window, 'resize', ThemeUtils.debounce(() => this._onResize(), 150));
    this.events.register(this.el, 'submit', e => this._onSubmit(e));
    this.events.register(this.input, 'input', ThemeUtils.debounce(() => this._onChange(), 150));
    this.events.register(this.input, 'focus', e => this._onFocus(e));
    this.events.register(this.input, 'keydown', e => this._onKeydown(e));

    // We want the flydown to close not only when you click outside but also when you tab outside.
    // `focusout` doesn't work as expected so we'll use `focusin`.
    this.events.register(document.body, 'focusin', () => this.close());
    this.events.register(this.el, 'focusin', e => e.stopPropagation());
    this.events.register(document.body, 'click', e => {
      if (e.target === this.searchCloseButton || !e.target.closest('[data-header-search-form]')) {
        this.close();
      }
    });
  }

  get _searchQuery() {
    return this.input.value.trim();
  }

  _onChange() {
    if (this._searchQuery.length === 0) {
      this.close();
      return;
    }

    this._getSearchResults(this._searchQuery);
  }

  _onFocus() {
    if (this._searchQuery.length === 0) return;

    this._getSearchResults(this._searchQuery);
  }

  _onSubmit(e) {
    if (this._searchQuery.length === 0) e.preventDefault();
  }

  _onKeyUp(e) {
    if (e.code === 'Escape') this.close();
  }

  _onKeydown(e) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (e.code === 'ArrowUp' || e.code === 'ArrowDown') {
      e.preventDefault();
    }
  }

  _onResize() {
    this._resizeSearchResults();
  }

  _clearResults() {
    const el = this.predictiveSearchResultsContainer;

    // This should be more performant than `innerHTMl = ""`
    while (el.firstChild) el.removeChild(el.firstChild);
  }

  _getSearchResults(searchTerm) {
    const cachedResult = this.cachedResults.get(searchTerm);

    this._setLoadingState();

    if (cachedResult) {
      this._renderSearchResults(cachedResult);
      return;
    }

    fetch(`${window.Theme.routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`)
      .then(response => {
        if (!response.ok) {
          this.close();
          throw new Error(response.status);
        }
        return response.text();
      })
      .then(text => {
        this.cachedResults.set(searchTerm, text);
        this._renderSearchResults(text);
      })
      .catch(error => {
        this.close();
        throw error;
      });
  }

  _setLoadingState() {
    this.predictiveSearchContainer.setAttribute('data-loading', 'true');
    this._clearResults();
    if (!this.isOpen) this._open();
  }

  _removeLoadingState() {
    this.predictiveSearchContainer.setAttribute('data-loading', 'false');
  }

  _resizeSearchResults() {
    const offset = 30;
    const position = this.input.getBoundingClientRect();
    const maxHeight = window.innerHeight - position.bottom - offset;

    this.predictiveSearchContainer.style.setProperty('--container-max-height', `${maxHeight}px`);
  }

  _renderSearchResults(resultsMarkup) {
    this._removeLoadingState();
    this.predictiveSearchResultsContainer.innerHTML = resultsMarkup;
    this._resizeSearchResults();
    rimg.watch(this.predictiveSearchResultsContainer);
  }

  _open() {
    if (this.isOpen) return;

    this.predictiveSearchContainerAnimation.animateTo('open')
      .then(() => {
        ScrollLock.lock(this.predictiveSearchContainer);
        this.siteOverlayAnimation.animateTo('open');
        this.isOpen = true;
      });
  }

  close() {
    if (!this.isOpen) return false;

    return this.predictiveSearchContainerAnimation.animateTo('closed')
      .then(() => {
        ScrollLock.unlock();
        this.siteOverlayAnimation.animateTo('closed');
        this._clearResults();
        this.isOpen = false;
      });
  }
}
